/**
 * E4 · সাপোর্ট — `/activity/:id/support`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame E4 (360 × 680).
 *
 * Frame note: "Five named problems in the user's words, the order attached
 * automatically. Human escalation is a visible button, not something earned after three
 * AI replies."
 *
 * WIRED. `GET /v1/activity/:id` carries `support`: the header, the subject line quoting
 * THIS order, the five drawn problems, the amber AI card and the escalation button. One
 * read with E2, so the support door and the detail can never disagree about which order
 * is being complained about.
 *
 * "কিছু পণ্য কম আছে" opens the same refund terms H2 draws — H2's own note says the terms
 * appear in this flow too, "দুই জায়গায় দুই কথা নয়" — and WHICH terms is a
 * `refund_policy_key` the server sends, not a `p.id === 'short'` branch here. A policy
 * decision living in a transcription is a policy decision nobody can change centrally.
 *
 * `POST /v1/support/tickets` is what a tap does. The escalation button sends the same
 * ticket with `escalate_to_human: true` — the frame note insists it is "a visible button,
 * not something earned after three AI replies", and it is one call, so a person who taps
 * it never describes the problem twice.
 *
 * DEVIATION: the escalation button is drawn `height:50px`, which § 02's Button does not
 * have (52 / 48 / 44). Built at 48 (`md`), which keeps the 1.5px teal rule and the 15px
 * type the frame draws. Reported.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import type { ActivityDetail } from '@oprappo/contracts';
import { Frame, RowChevron, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchActivity, openSupportTicket } from './api.js';

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function E4SupportView({
  data,
  activityRef,
}: {
  data: ActivityDetail | null;
  activityRef: string;
}): JSX.Element {
  const navigate = useNavigate();
  const id = activityRef === '' ? undefined : activityRef;
  const ref = activityRef;
  const support = data?.support ?? null;

  /**
   * Tapping a problem opens a ticket AND, where the server says so, the refund terms.
   *
   * `refund_policy_key` comes from the server rather than from a `p.id === 'short'`
   * branch here — H2's own note is that the terms must read the same in both places,
   * "দুই জায়গায় দুই কথা নয়", and a client-side branch is a second place for them to
   * disagree.
   */
  const choose = (problemId: string, refundPolicyKey: string | null): void => {
    void openSupportTicket({
      activity_id: ref,
      problem_id: problemId,
      escalate_to_human: false,
    }).then(() => {
      if (refundPolicyKey !== null) navigate(`/policy/refund/${refundPolicyKey}`);
    });
  };

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader
        titleBn={support?.title_bn ?? ''}
        onBack={() => navigate(id === undefined ? '/activity' : `/activity/${id}`)}
      />

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="bg-white border border-border p-[13px] mb-[20px]">
          <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted">
            {support?.subject_label_bn ?? ''}
          </div>
          <div className="font-anek font-medium text-[15.5px] leading-[1.75] text-ink mt-[5px]">
            {support?.subject_bn ?? ''}
          </div>
        </div>

        <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink mb-[11px]">
          {support?.question_bn ?? ''}
        </div>
        <div className="flex flex-col gap-[10px]">
          {(support?.problems ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => choose(p.id, p.refund_policy_key)}
              className="bg-white border border-border p-[14px] flex justify-between items-center gap-[11px] w-full text-left rounded-none"
            >
              <span className="font-anek font-normal text-[15px] leading-[1.75] text-ink2">
                {p.label_bn}
              </span>
              <RowChevron size={16} />
            </button>
          ))}
        </div>
      </div>

      <div className="px-[16px] pb-[16px]">
        <div className="bg-white border border-amber p-[13px] flex gap-[12px] items-center mb-[10px]">
          <svg width={21} height={21} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
            <path
              d="M12 3l2.2 6.2L20.5 11l-6.3 1.8L12 19l-2.2-6.2L3.5 11l6.3-1.8z"
              stroke="#8A5A0C"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          </svg>
          <div className="flex-1 font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">
            {support?.ai_bn ?? ''}
          </div>
        </div>
        {/* Drawn `height:50px` with a `1.5px` rule. The canvas is content-box outside a
            frame box, so that is a 52px outer box; written as the outer height because
            `w-full` and content-box cannot both hold. `p-0`: preflight is off, so a
            <button> keeps the UA's own `padding:1px 6px`. `md` supplies the 15px type
            and the 1.5px teal rule. */}
        <Button
          tone="secondary"
          size="md"
          className="h-[52px] p-0"
          onClick={() =>
            void openSupportTicket({
              activity_id: ref,
              problem_id: 'other',
              escalate_to_human: true,
            })
          }
        >
          {support?.human_bn ?? ''}
        </Button>
      </div>
    </Frame>
  );
}

export function E4Support(): JSX.Element {
  const { id } = useParams();
  const ref = id ?? '';
  const load = useCallback((signal: AbortSignal) => fetchActivity(ref, signal), [ref]);
  const { data, error } = useResource(load, ref);

  return (
    <SurfaceStates surface="activity" error={error ?? undefined}>
      <E4SupportView data={data} activityRef={ref} />
    </SurfaceStates>
  );
}
